import { cn } from "@/utils/cn";
import type { ButtonHTMLAttributes, ReactNode } from "react";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "outline";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export function Button({
  children,
  variant = "primary",
  size = "md",
  loading,
  leftIcon,
  rightIcon,
  className,
  disabled,
  ...props
}: ButtonProps) {
  const variants = {
    primary:
      "bg-[#ff0000] text-white hover:bg-[#e00000] border border-transparent shadow-sm",
    secondary:
      "bg-white text-[#ff0000] hover:bg-red-50 border border-red-200 shadow-sm",
    outline:
      "bg-white text-[#374151] hover:bg-gray-50 border border-[#E2E8F0] shadow-sm",
    ghost:
      "bg-transparent text-[#5A6A85] hover:bg-gray-100 border border-transparent",
    danger:
      "bg-red-600 text-white hover:bg-red-700 border border-transparent shadow-sm",
  };

  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-sm",
  };

  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#ff0000]/20 disabled:opacity-50 disabled:pointer-events-none active:scale-[0.97]",
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <div className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
      )}
      {!loading && leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
}
